<script setup lang="ts">
import { CommonBlogListSlider } from "#components";
import { ref, onMounted } from "vue";
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const props = defineProps<{
  blogData: any;
}>();
const blog = computed(() => props.blogData);
function formatDate(data: string) {
  if (data) {
    const date = new Date(data); // Parse the input date string
    const day = String(date.getDate()).padStart(2, "0"); // Ensure 2-digit day
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2-digit month
    const year = date.getFullYear(); // Get the full year

    return `${day}/${month}/${year}`;
  }
}
const { apiClient } = useShopwareContext();
const loading = ref(true);
const error = ref("");
const blogList = ref(null);

async function fetchBlogDetails() {
  loading.value = true;
  error.value = "";
  try {
    const response = await apiClient.invoke(
      "getBlogsLists post /bloglisting/{navigationId}",
      {
        pathParams: { navigationId: blog.value?.categoryid },
      }
    );
    blogList.value = response?.data || {};
  } catch (err) {
    error.value = err.message || "Failed to load blog details.s";
    console.error("Error loading blog:", err);
  } finally {
    loading.value = false;
  }
}

watch(
  () => blog.value?.categoryid,
  (newCategoryId) => {
    if (newCategoryId) {
      fetchBlogDetails();
    }
  },
  { immediate: true } // Runs on mount if categoryid exists
);
</script>

<template>
  <section>
    <div class="blog px-4">
      <div class="container mx-auto relative pt-8 pb-6">
        <div class="post-wrap">
          <h1
            class="font-bold text-2xl md:text-3xl lg:text-4xl text-dark-bg mb-4"
          >
            {{ blog.title }}
          </h1>
          <div
            class="font-medium text-base md:text-lg lg:text-xl text-dark-bg mb-4 leading-normal p-0 sm:pr-12 md:pr-32 lg:pr-52"
          >
            <p class="text-medium-grey line-clamp-2">
              {{ blog.teaser }}. {{ blog.contents.replace(/<[^>]*>/g, "").substring(0, 300) }}
            </p>
          </div>

          <div class="post-by-read mb-5 lg:mb-[12px] flex items-center">
            <div v-if="blog.author" class="post-detail flex items-center mr-5">
              <div class="profile rounded-lg mr-4">
                <img
                  v-if="blog.author?.image?.url"
                  :src="blog.author?.image?.url"
                  :alt="blog.author?.name"
                  class="w-[22px] h-[22px] rounded-full"
                />
                <img
                  v-else
                  src="assets/images/blog/john-smith.png"
                  :alt="blog.author?.name"
                  class="w-[22px] h-[22px] rounded-full"
                />
              </div>
              <div class="name">
                <span
                  class="text-xs font-semibold leading-normal text-davy-grey"
                  >{{ blog.author?.name }}</span
                >
              </div>
            </div>
            <div class="read">
              <span
                v-if="blog.postdate"
                class="text-xs font-semibold leading-normal text-davy-grey relative"
              >
                {{ $t("news.posted") }} : {{ formatDate(blog.postdate) }}
              </span>
            </div>
          </div>
          <p v-if="blog.tags" class="text-medium-grey mb-10 lg:mb-[52px] ml-1">
            <i class="fa fa-tags mr-4"></i>
            <template v-for="tag in blog.tags">
              <span>{{ tag.name }}</span>
              <span v-if="tag !== blog.tags[blog.tags.length - 1]">, </span>
            </template>
          </p>
          <div v-if="blog.image" class="post-img mb-10 lg:mb-[52px]">
            <figure>
              <NuxtImg
                :src="blog.image?.url"
                :alt="blog.image?.alt || blog.title"
                :width="blog.image?.metaData?.width ?? 220"
                :height="blog.image?.metaData?.height ?? 220"
                loading="lazy"
                format="webp"
                class="w-full h-auto rounded-lg"
              />
            </figure>
          </div>

          <div
            class="font-medium text-base md:text-lg lg:text-xl text-dark-bg mb-4 leading-normal p-0 sm:pr-12 md:pr-32 lg:pr-52"
            v-html="blog.contents"
          ></div>
          <template v-if="blog.items">
            <div
              v-for="item in blog.items"
              class="mt-10 font-medium text-base md:text-lg lg:text-xl text-dark-bg mb-4 leading-normal p-0 sm:pr-12 md:pr-32 lg:pr-52"
            >
              <h5 class="font-bold text-xl md:text-3xl text-dark-bg mb-4">
                {{ item.title }}
              </h5>
              <div v-html="item.content"></div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="bestseller px-4">
      <div class="container mx-auto relative pt-12 pb-24 sm:py-24">
        <div class="inner-wrap" v-if="blogList?.elements">
          <CommonBlogListSlider
            :blogs="blogList.elements"
            :currentId="blog.id"
          />
        </div>
      </div>
    </div>
  </section>
</template>
